import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'

import styles from './_styles/card-content.scss?inline'

/**
 * `dap-ds-card-content`
 * @summary A card content is a container for content in a card.
 * @element dap-ds-card-content
 * @title - Card content
 * @group card
 *
 * @property {'sm' | 'md' | 'lg'} size - The size of the card subtitle. Default is `sm`.
 * @property {'top' | 'bottom' | 'both' | 'none'} spacing - The spacing of the card content. This adds a margin to the card subtitle. Default is `bottom`.
 * @property {string} sizeMap - Responsive size map (e.g. "md:lg").
 *
 * @slot - The content of the card-content.
 *
 * @csspart base - The main card content container.
 *
 * @cssproperty --dds-card-content-padding - The padding of the card content. (default: 0)
 * @cssproperty --dds-card-content-margin - The margin of the card content. (default: 0)
 * @cssproperty --dds-card-content-font-size - The font size of the card content. (default: var(--dds-font-base))
 * @cssproperty --dds-card-content-line-height - The line height of the card content. (default: var(--dds-font-line-height-xlarge))
 * @cssproperty --dds-card-content-spacing-lg - The large spacing value. (default: var(--dds-spacing-600))
 * @cssproperty --dds-card-content-spacing-md - The medium spacing value. (default: var(--dds-spacing-400))
 * @cssproperty --dds-card-content-spacing-sm - The small spacing value. (default: var(--dds-spacing-400))
 * @cssproperty --dds-card-content-spacing-top - The top spacing value. (default: 0)
 * @cssproperty --dds-card-content-spacing-bottom - The bottom spacing value. (default: 0)
 */
@customElement('dap-ds-card-content')
export default class DapDSCardContent extends SizedMixin(DdsElement) {
  /** The base rendered root tag of the card content. */
  @property() renderAs = 'div'

  static readonly styles = unsafeCSS(styles)

  constructor() {
    super()
    this.spacing = 'bottom'
    this.parentSized = 'true'
  }

  render() {
    return html`
      <${unsafeStatic(this.renderAs)} part="base" class=${clsx(
        'card-content',
        `card-content--${this.effectiveSize}`,
        `card-content-spacing--${this.spacing}`,
      )}>
        <slot> </slot>
      </${unsafeStatic(this.renderAs)}>
    `
  }
}
